import React from "react";

const TOKEN = /\[(\/?)(b|i|u|s|color|size|url|img|quote|code|center|left|right|list|\*)(?:=([^\]]*))?\]/gi;
const COLOR_RE = /^(#[0-9a-f]{3,8}|[a-z]+)$/i;
const SAFE_URL = /^https?:\/\/[^\s"'<>]+$/i;
const BLOCK_TAGS = new Set(["quote", "code", "center", "left", "right", "list"]);
const URL_RE = /(https?:\/\/[^\s<>"'\]]+)/g;

export const hasBBCode = (text) => /\[(b|i|u|s|color|size|url|img|quote|code|center|left|right|list)(=[^\]]*)?\]/i.test(text || "");

const tokenize = (src) => {
  const tokens = [];
  let last = 0;
  let m;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(src))) {
    if (m.index > last) tokens.push({ type: "text", value: src.slice(last, m.index) });
    tokens.push({ type: m[1] ? "close" : "open", tag: m[2].toLowerCase(), arg: m[3], raw: m[0] });
    last = m.index + m[0].length;
  }
  if (last < src.length) tokens.push({ type: "text", value: src.slice(last) });
  return tokens;
};

// Builds a tree; unmatched tags degrade to plain text.
const parse = (tokens) => {
  const root = { children: [] };
  const stack = [root];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const top = stack[stack.length - 1];
    if (top.tag === "code" && !(t.type === "close" && t.tag === "code")) {
      top.children.push({ text: t.type === "text" ? t.value : t.raw });
      continue;
    }
    if (t.type === "text") top.children.push({ text: t.value });
    else if (t.type === "open") {
      if (t.tag === "*") top.children.push({ tag: "*", children: [], item: true });
      else {
        const node = { tag: t.tag, arg: t.arg, children: [], raw: t.raw };
        top.children.push(node);
        stack.push(node);
      }
    } else if (t.tag !== "*") {
      const idx = stack.map((n) => n.tag).lastIndexOf(t.tag);
      if (idx > 0) stack.length = idx;
      else top.children.push({ text: t.raw });
    }
  }
  return root;
};

const linkify = (text, key) => {
  const parts = text.split(URL_RE);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <a key={`${key}-${i}`} href={p} target="_blank" rel="noopener noreferrer nofollow ugc">{p}</a>
    ) : (
      <React.Fragment key={`${key}-${i}`}>{p}</React.Fragment>
    )
  );
};

const plain = (node) => (node.text !== undefined ? node.text : (node.children || []).map(plain).join(""));

const renderNodes = (nodes, keyPrefix = "n") =>
  nodes.map((node, i) => renderNode(node, `${keyPrefix}-${i}`));

const renderNode = (node, key) => {
  if (node.text !== undefined) return <React.Fragment key={key}>{linkify(node.text, key)}</React.Fragment>;
  const kids = () => renderNodes(node.children, key);
  switch (node.tag) {
    case "b": return <strong key={key}>{kids()}</strong>;
    case "i": return <em key={key}>{kids()}</em>;
    case "u": return <u key={key}>{kids()}</u>;
    case "s": return <s key={key}>{kids()}</s>;
    case "color":
      return COLOR_RE.test(node.arg || "") ? <span key={key} style={{ color: node.arg }}>{kids()}</span> : <span key={key}>{kids()}</span>;
    case "size": {
      const n = Math.min(32, Math.max(10, parseInt(node.arg, 10) || 15));
      return <span key={key} style={{ fontSize: `${n}px` }}>{kids()}</span>;
    }
    case "url": {
      const href = (node.arg || plain(node)).trim();
      if (!SAFE_URL.test(href)) return <span key={key}>{kids()}</span>;
      return <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow ugc">{node.arg ? kids() : href}</a>;
    }
    case "img": {
      const src = plain(node).trim();
      if (!SAFE_URL.test(src)) return <span key={key}>{node.raw}{src}</span>;
      return <img key={key} src={src} alt="" loading="lazy" referrerPolicy="no-referrer" className="my-2 inline-block max-h-[480px] max-w-full rounded-xl" />;
    }
    case "quote":
      return (
        <blockquote key={key} className="my-2 border-l-2 border-white/20 pl-3 text-white/60">
          {node.arg && <div className="mb-1 font-semibold text-white/80">{node.arg} írta:</div>}
          {kids()}
        </blockquote>
      );
    case "code":
      return <pre key={key} className="my-2 overflow-x-auto rounded-xl bg-black/40 p-3 font-mono text-xs">{plain(node)}</pre>;
    case "center": return <div key={key} style={{ textAlign: "center" }}>{kids()}</div>;
    case "left": return <div key={key} style={{ textAlign: "left" }}>{kids()}</div>;
    case "right": return <div key={key} style={{ textAlign: "right" }}>{kids()}</div>;
    case "list": {
      const items = [];
      let cur = null;
      node.children.forEach((c) => {
        if (c.item) { cur = []; items.push(cur); } else if (cur) cur.push(c);
      });
      return (
        <ul key={key} className="my-2 list-disc pl-6">
          {items.map((it, i) => <li key={i}>{renderNodes(it, `${key}-${i}`)}</li>)}
        </ul>
      );
    }
    default: return <React.Fragment key={key}>{kids()}</React.Fragment>;
  }
};

export const BBCode = ({ children }) => {
  const tree = parse(tokenize(children || ""));
  return <div className="whitespace-pre-wrap break-words">{renderNodes(tree.children)}</div>;
};

export { BLOCK_TAGS };
